// src/pages/Notice.js
import React, { useState, useContext, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import TabContext from "@/contexts/TabContext";
import { useListPageFilter, dateFormatToISOString } from "@/utils";
import { PageTabs, Pagination, Toast } from "@/components";
import { useGetNoticeListQuery } from "@/redux/noticeApi";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";
import Select from "react-select";

const StatusOptions = [
    { value: "", label: "전체" },
    { value: "ACTIVE", label: "사용" },
    { value: "INACTIVE", label: "미사용" },
];

// 페이지 기본 필터
const pageFilter = {
    parameters: {
        page: 0,
        size: 20,
        query: "",
        status: "",
        started_at: "",
        ended_at: "",
    },
};

export const Notice = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state: tabs, dispatch } = useContext(TabContext);

    const { formData, handleChange, setFormData, parameters, toastMsg } = useListPageFilter(pageFilter, {
        triggerSearch: true,
    });

    const { triggerSearch } = formData;

    // 컴포넌트 마운트 시 초기 데이터 조회
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            triggerSearch: true,
        }));
    }, []);

    // 탭 추가 로직
    useEffect(() => {
        const handleTabAdd = () => {
            const existingTab = tabs.find((tab) => tab.url.split("?")[0] === location.pathname);
            if (!existingTab) {
                dispatch({
                    type: "ADD_TAB",
                    payload: { url: location.pathname + location.search, title: "공지사항" },
                });
            }
        };
        handleTabAdd();
    }, [dispatch, location.pathname, location.search, tabs]);

    const { data: data, error } = useGetNoticeListQuery(parameters, {
        refetchOnMountOrArgChange: false,
        skip: !triggerSearch,
    });

    // 검색 버튼 클릭
    const handleSearch = () => {
        setFormData((prev) => ({
            ...prev,
            parameters: { ...prev.parameters, page: 0 },
            triggerSearch: true,
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management container">
                <PageTabs />

                <div className="table-container">
                    <div className="table-head">
                        <span className="head-tit">공지사항</span>
                        <div className="breadcrumbs">
                            <ul>
                                <li className="home"></li>
                                <li>일반 관리</li>
                                <li>공지사항</li>
                            </ul>
                        </div>
                    </div>

                    {/* 검색/필터 영역 */}
                    <TableFilter formData={formData} setFormData={setFormData} handleSearch={handleSearch} />

                    <div className="table-content">
                        <div className="table-option">
                            <div className="table-caption">
                                <span className="data-count">
                                    {data && data.pagination ? `전체 ${data.pagination.totalElements}건` : ""}
                                </span>
                            </div>
                            <div className="table-buttons"></div>
                        </div>

                        <div className="table-wrapper">
                            <div className="table">
                                <table>
                                    <colgroup>
                                        <col width="8%" />
                                        <col width="67%" />
                                        <col width="15%" />
                                        <col width="10%" />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>
                                                <div className="cell">
                                                    <span className="cell-tit">번호</span>
                                                </div>
                                            </th>
                                            <th>
                                                <div className="cell">
                                                    <span className="cell-tit">제목</span>
                                                </div>
                                            </th>
                                            <th>
                                                <div className="cell">
                                                    <span className="cell-tit">등록일시</span>
                                                </div>
                                            </th>
                                            <th>
                                                <div className="cell">
                                                    <span className="cell-tit">등록자</span>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data && data.data && data.data.length > 0 ? (
                                            data.data.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-tag text-blue">{item.id}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cell" data-align="left">
                                                            <span
                                                                className="cell-link"
                                                                onClick={() => {
                                                                    navigate(`/admin/notice/${item.id}`);
                                                                }}
                                                            >
                                                                {item.title}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">2024-02-24 12:20</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">홍길동</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="data-empty">
                                                    <div className="cell" data-align="center">
                                                        <span className="cell-desc">데이터가 없습니다.</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {data && data.pagination && data.data && data.data.length > 0 && (
                            <Pagination pagination={data.pagination} setFormData={setFormData} />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

/** 필터 컴포넌트 (VehicleManage.js와 유사) */
const TableFilter = ({ formData, setFormData, handleSearch }) => {
    const { parameters } = formData;
    const [activeButton, setActiveButton] = useState(null);

    const handleParameterChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                [name]: value,
            },
            triggerSearch: false,
        }));
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setFormData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                page: 0,
                started_at: start ? dateFormatToISOString(start, "start") : "",
                ended_at: end ? dateFormatToISOString(end, "end") : "",
            },
            triggerSearch: false,
        }));
        setActiveButton(null);
    };

    const setDateRange = (days, buttonIndex) => {
        const now = new Date();
        const start = new Date(now);
        start.setDate(start.getDate() - days);
        setFormData((prev) => ({
            ...prev,
            parameters: {
                ...prev.parameters,
                page: 0,
                started_at: dateFormatToISOString(start, "start"),
                ended_at: dateFormatToISOString(now, "end"),
            },
            triggerSearch: false,
        }));
        setActiveButton(buttonIndex);
    };

    return (
        <div className="table-filter">
            <div className="filter-row">
                <div className="filter-item">
                    <div className="item-head">
                        <span className="head-tit">상태 :</span>
                    </div>
                    <div className="item-content">
                        <div className="item-select">
                            <Select
                                options={StatusOptions}
                                className="filter-select"
                                classNamePrefix="filter-select"
                                isSearchable={false}
                                placeholder="상태 선택"
                                value={StatusOptions.find((opt) => opt.value === parameters.status)}
                                onChange={(option) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parameters: { ...prev.parameters, status: option.value, page: 0 },
                                        triggerSearch: false,
                                    }));
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="filter-item">
                    <div className="item-head">
                        <span className="head-tit">등록일 :</span>
                    </div>
                    <div className="item-content">
                        <div className="item-input">
                            <DatePicker
                                locale={ko}
                                showYearDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={20}
                                dateFormat="yyyy.MM.dd"
                                onKeyDown={(e) => e.preventDefault()}
                                selectsRange={true}
                                startDate={parameters.started_at ? new Date(parameters.started_at) : null}
                                endDate={parameters.ended_at ? new Date(parameters.ended_at) : null}
                                onChange={(e) => handleDateChange(e)}
                            />
                        </div>
                    </div>
                </div>

                <div className="filter-item">
                    <div className="item-content">
                        <div className="item-buttons">
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 0 ? "active" : ""}`}
                                onClick={() => setDateRange(0, 0)}
                            >
                                당일
                            </button>
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 15 ? "active" : ""}`}
                                onClick={() => setDateRange(15, 15)}
                            >
                                15일
                            </button>
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 30 ? "active" : ""}`}
                                onClick={() => setDateRange(30, 30)}
                            >
                                1개월
                            </button>
                            <button
                                type="button"
                                className={`btn-toggle ${activeButton === 90 ? "active" : ""}`}
                                onClick={() => setDateRange(90, 90)}
                            >
                                3개월
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="filter-row">
                <div className="filter-item">
                    <div className="item-content">
                        <div className="item-input" style={{ width: "300px" }}>
                            <input
                                type="text"
                                placeholder="입력하세요."
                                name="query"
                                value={parameters.query}
                                onChange={handleParameterChange}
                            />
                        </div>
                        <div className="item-buttons">
                            <button type="button" className="btn-search" onClick={handleSearch}>
                                검색
                            </button>
                            <button
                                type="button"
                                className="btn-search"
                                onClick={() => {
                                    // 필터 초기화
                                    setFormData((prev) => ({
                                        ...prev,
                                        parameters: { ...pageFilter.parameters },
                                    }));
                                    setActiveButton(null);
                                }}
                            >
                                초기화
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
